import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

interface Snackbar {
    message: string;
    timeout?: number;
    type: TYPE_ALERT;
    button?: string;
    action?: () => void;
}

export type TYPE_ALERT = 'none' | 'error' | 'info' | 'warning' | 'danger';

@Injectable()
export class SnackbarService {

    private readonly DEFAULT_TIMEOUT = 5000;

    private readonly DEFAULT_THEME = 'none';

    /**
     * Helper classes by notification type
     * @type {object}
     */
    private readonly helperClasses: object = {
        none: 'syn-box--default--invert',
        success: 'syn-box--success--invert',
        info: 'syn-box--info--invert',
        warning: 'syn-box--warning--invert',
        danger: 'syn-box--danger--invert'
    };

    constructor(public snackBar: MatSnackBar) {
    }

    /**
     * Show Snackbar
     * message: Main message
     * timeout: Snackbar duration
     * type: Snackbar type to add helper class
     * button: Action button text
     * action: Action handler
     * @param data
     */
    show(data: Snackbar) {

        if (!data || !data.message) {
            throw new Error('Please provide a data object with at least a message to display.');
        }
        if (data.action && !data.button) {
            throw new Error('Please provide action text with the handler.');
        }

        const duration = data.timeout ? data.timeout : this.DEFAULT_TIMEOUT;

        // The 'extraClasses' property currently does not automatically target
        // all children components and the given styles may thus not be displayed.
        // Reference : https://github.com/angular/material2/issues/6087
        const panelClass = [this.helperClasses[data.type || this.DEFAULT_THEME]];
        const snackBarRef = this.snackBar.open(data.message, data.button, {
            duration,
            panelClass
        });

        if (data.action) {
            snackBarRef.onAction().subscribe(data.action);
        }
    }

    openFromComponent(
        component: ComponentType<unknown>,
        data: { data: {[key: string]: string }; options: { type: TYPE_ALERT; timeout: number; action?: () => void; button?: string}}
    ) {
        if (!data.data || !data.data.message) {
            throw new Error('Please provide a data object with at least a message to display.');
        }
        if (data.options.action && !data.options.button) {
            throw new Error('Please provide action text with the handler.');
        }

        const duration = data.options.timeout ? data.options.timeout : this.DEFAULT_TIMEOUT;

        // The 'extraClasses' property currently does not automatically target
        // all children components and the given styles may thus not be displayed.
        // Reference : https://github.com/angular/material2/issues/6087
        const panelClass = [this.helperClasses[data.options.type || this.DEFAULT_THEME]];
        const snackBarRef = this.snackBar.openFromComponent(component,  {
            data : {
                ...data.data
            },
            ...data.options,
            duration,
            panelClass
        });

        if (data.options.action) {
            snackBarRef.onAction().subscribe(data.options.action);
        }
    }

}
